import { DOCUMENT_DOWNLOAD_LINK, GENERATE_POLICY_DOCUMENT, GENERATE_QUOTE_DOCUMENT } from '~/graphql/auto/documents'

export const AutoDocumentTypes = Object.freeze({
  UNSPECIFIED: 'UNSPECIFIED',
  QUOTE_PROPOSAL: 'QUOTE_PROPOSAL',
  RCE_DETAILS: 'RCE_DETAILS',
  APPLICATION: 'APPLICATION',
  DECLARATION_PAGE: 'DECLARATION_PAGE',
  AUTO_ID_CARD: 'AUTO_ID_CARD',
})

class AutoDocumentsAPI {
  constructor(nuxtApp) {
    this._nuxtApp = nuxtApp
  }

  // Unlike the standard documents API (which downloads bytes from a REST endpoint),
  // auto docs return short-lived signed URLs via GraphQL.
  // We use the browser URL directly to let the provider handle file delivery.

  _openSignedUrl(downloadUrl, expiresAt, fileName) {
    if (!downloadUrl) {
      throw new Error('No download URL returned.')
    }
    if (expiresAt && new Date(expiresAt) <= new Date()) {
      throw new Error('Document URL has expired.')
    }
    const anchor = document.createElement('a')
    anchor.href = downloadUrl
    anchor.download = fileName
    anchor.rel = 'noopener noreferrer'
    anchor.target = '_blank'
    document.body.appendChild(anchor)
    anchor.click()
    anchor.remove()
  }

  // Errors are thrown and handled by the caller.
  async downloadDocument(path, fileName) {
    const { error, data } = await this._nuxtApp.$urql.client
      .query(DOCUMENT_DOWNLOAD_LINK, { documentPath: path }, { skipGlobalErrorHandling: true })
      .toPromise()

    if (error) {
      throw new Error('Unable to retrieve a download link for this document.')
    }

    const downloadUrl = data?.documentDownloadLink?.url
    const expiresAt = data?.documentDownloadLink?.expiresAt

    this._openSignedUrl(downloadUrl, expiresAt, fileName)

    return {
      success: true,
    }
  }

  // Errors are thrown and handled by the caller.
  async generateAndOpenQuoteDocument(transactionUUID, documentType, fileName) {
    const { error, data } = await this._nuxtApp.$appLock.lockUntil(
      this._nuxtApp.$urql.client
        .mutation(
          GENERATE_QUOTE_DOCUMENT,
          { transactionUuid: transactionUUID, documentType },
          { skipGlobalErrorHandling: true }
        )
        .toPromise()
    )

    if (error) {
      throw new Error('Unable to generate the quote document.')
    }

    const downloadUrl = data?.generateQuoteDocument?.downloadLink?.url
    const expiresAt = data?.generateQuoteDocument?.downloadLink?.expiresAt

    this._openSignedUrl(downloadUrl, expiresAt, fileName)

    return {
      success: true,
    }
  }

  // Errors are thrown and handled by the caller.
  async generateAndOpenPolicyDocument(policyId, documentType, fileName) {
    const { error, data } = await this._nuxtApp.$appLock.lockUntil(
      this._nuxtApp.$urql.client
        .mutation(
          GENERATE_POLICY_DOCUMENT,
          { policyId, documentType },
          { skipGlobalErrorHandling: true }
        )
        .toPromise()
    )

    if (error) {
      throw new Error('Unable to generate the policy document.')
    }

    const downloadUrl = data?.generatePolicyDocument?.downloadLink?.url
    const expiresAt = data?.generatePolicyDocument?.downloadLink?.expiresAt

    this._openSignedUrl(downloadUrl, expiresAt, fileName)

    return {
      success: true,
    }
  }
}

export default defineNuxtPlugin((nuxtApp) => {
  return {
    provide: {
      autoDocumentsAPI: new AutoDocumentsAPI(nuxtApp),
    },
  }
})
